package br.com.htms.inspeccaocampo.data.os

/** Perigo/fator de risco como consta no inventário do PGR. */
data class PgrRisco(
    val grupo: String,
    val perigo: String,
    val danos: String,
    val epc: List<String>,
    val epi: List<String>,
    val medidasAdm: String
)

data class PgrCargo(val nome: String, val descricao: String)

/** Setor do PGR; [ghe] segue a ordem dos setores no documento (GHE 01, 02, ...). */
data class PgrSetor(
    val ghe: Int,
    val nome: String,
    val descricao: String,
    val riscos: List<PgrRisco>,
    val cargos: List<PgrCargo>
) {
    val gheRotulo: String get() = "GHE " + ghe.toString().padStart(2, '0')
}

/** Cargo + setor: o mesmo cargo aparece em mais de um GHE (ex.: Ajudante, Pedreiro). */
data class PgrCargoOpcao(val setor: PgrSetor, val cargo: PgrCargo) {
    val id: String get() = "${setor.ghe}|${cargo.nome}"
    val setorGhe: String get() = "${setor.nome} - ${setor.gheRotulo}"
}

enum class OsCategoria(val rotulo: String, val corRgb: Int) {
    FISICOS("FÍSICOS", 0x92D050),
    QUIMICOS("QUÍMICOS", 0xFF0000),
    ACIDENTES("ACIDENTES", 0x0070C0),
    BIOLOGICO("BIOLÓGICO", 0x984806),
    ERGONOMICO("ERGONÔMICO", 0xFFFF00)
}

data class OsItemRisco(val numero: Int, val risco: String, val dano: String, val medida: String)

data class OsBlocoRisco(val categoria: OsCategoria, val itens: List<OsItemRisco>)

data class OrdemServicoDados(
    val nome: String,
    val funcao: String,
    val setorGhe: String,
    val empresa: String,
    val admissao: String,
    val blocos: List<OsBlocoRisco>,
    val descricaoFuncao: String,
    val epis: List<String>,
    val localData: String
)

object PgrCatalog {
    val fonte: String get() = PGR_FONTE
    val setores: List<PgrSetor> get() = PGR_SETORES

    val opcoes: List<PgrCargoOpcao> by lazy {
        setores.flatMap { s -> s.cargos.map { PgrCargoOpcao(s, it) } }
            .sortedWith(compareBy({ normalizar(it.cargo.nome) }, { it.setor.ghe }))
    }

    fun buscar(consulta: String): List<PgrCargoOpcao> {
        val q = normalizar(consulta)
        if (q.isBlank()) return opcoes
        val termos = q.split(' ').filter { it.isNotBlank() }
        return opcoes.filter { op ->
            val alvo = normalizar("${op.cargo.nome} ${op.setor.nome} ${op.setor.gheRotulo}")
            termos.all { it in alvo }
        }
    }

    /** Riscos do setor + riscos gerais da unidade, numerados em sequência como no modelo da OS. */
    fun montarBlocos(opcao: PgrCargoOpcao): List<OsBlocoRisco> {
        val todos = opcao.setor.riscos + PGR_RISCOS_UNIDADE
        var n = 0
        return OsCategoria.entries.map { cat ->
            val doGrupo = todos.filter { categoriaDe(it.grupo) == cat }
                .distinctBy { normalizar(it.perigo) }
            val itens = if (doGrupo.isEmpty()) {
                listOf(OsItemRisco(++n, "Ausentes.", "Não se aplica.", "Não se aplica."))
            } else {
                doGrupo.map { r ->
                    OsItemRisco(
                        numero = ++n,
                        risco = r.perigo.trim(),
                        dano = r.danos.trim().ifBlank { "Não informado no PGR." },
                        medida = medidaDeControle(r)
                    )
                }
            }
            OsBlocoRisco(cat, itens)
        }
    }

    /** EPIs/EPCs citados nos riscos do setor, sem duplicar (CA já removido na extração). */
    fun episSugeridos(opcao: PgrCargoOpcao): List<String> {
        val vistos = mutableSetOf<String>()
        val out = mutableListOf<String>()
        (opcao.setor.riscos.flatMap { it.epi } + opcao.setor.riscos.flatMap { it.epc }).forEach { raw ->
            val item = raw.trim().trimEnd('.', ';').trim()
            if (item.isNotBlank() && vistos.add(normalizar(item))) out += item
        }
        return out
    }

    private fun categoriaDe(grupo: String): OsCategoria? = when {
        normalizar(grupo).startsWith("fisic") -> OsCategoria.FISICOS
        normalizar(grupo).startsWith("quimic") -> OsCategoria.QUIMICOS
        normalizar(grupo).startsWith("acident") -> OsCategoria.ACIDENTES
        normalizar(grupo).startsWith("biolog") -> OsCategoria.BIOLOGICO
        normalizar(grupo).startsWith("ergon") -> OsCategoria.ERGONOMICO
        else -> null
    }

    /**
     * Textos padrão do modelo "OS - Montador" para os perigos conhecidos; demais
     * perigos usam EPC/EPI/medidas administrativas do próprio PGR.
     */
    private val MEDIDAS_PADRAO: List<Pair<String, String>> = listOf(
        "ruido" to "Uso obrigatório de protetor auricular e controle da exposição ao ruído.",
        "contato com produtos quimicos" to "Uso de óculos, luvas e demais EPIs para evitar contato com produtos.",
        "trabalho em altura" to "Isolamento da área, sinalização e utilização dos EPIs obrigatórios.",
        "area de operac" to "Manter organização da área, utilizar ferramentas adequadas e EPIs.",
        "acidente de trajeto" to "Utilizar calçado de segurança e manter acessos livres de obstáculos.",
        "incendio" to "Utilizar proteções de máquinas, bloqueio de energia e EPIs específicos.",
        "choque eletrico" to "Treinamento e capacitação, executar serviços elétricos conforme NR-10, com bloqueio e desenergização.",
        "fatores ergonomicos" to "Ajuste ergonômico do posto de trabalho, pausas e orientação postural.",
        "condicoes de trabalho" to "Disponibilização de recursos adequados e ambiente seguro.",
        "demandas de trabalho" to "Planejamento das atividades e distribuição equilibrada das tarefas.",
        "equilibrio vida pessoal" to "Respeito às jornadas de trabalho e períodos de descanso.",
        "jornada de trabalho" to "Controle da jornada e cumprimento dos intervalos legais.",
        "lideranca e gestao" to "Comunicação clara, acompanhamento e suporte aos colaboradores.",
        "planejamento do trabalho" to "Definição prévia das atividades, prioridades e responsabilidades.",
        "relacoes interpessoais" to "Promoção do respeito mútuo e prevenção de conflitos.",
        "saude e bem estar" to "Incentivo à saúde ocupacional, qualidade de vida e apoio aos trabalhadores."
    )

    private fun medidaDeControle(r: PgrRisco): String {
        val chave = normalizar(r.perigo)
        MEDIDAS_PADRAO.firstOrNull { (k, _) -> k in chave }?.let { return it.second }
        val partes = buildList {
            if (r.epc.isNotEmpty()) add("EPC: " + r.epc.joinToString(", "))
            if (r.epi.isNotEmpty()) add("Uso de EPIs: " + r.epi.joinToString(", "))
            if (r.medidasAdm.isNotBlank()) add(r.medidasAdm.trim().trimEnd('.'))
        }
        return if (partes.isEmpty()) {
            "Seguir procedimentos internos e orientações de segurança."
        } else {
            partes.joinToString("; ") + "."
        }
    }

    fun normalizar(s: String): String {
        val sb = StringBuilder(s.length)
        for (ch in s.lowercase()) {
            sb.append(
                when (ch) {
                    'á', 'à', 'â', 'ã', 'ä' -> 'a'
                    'é', 'è', 'ê', 'ë' -> 'e'
                    'í', 'ì', 'î', 'ï' -> 'i'
                    'ó', 'ò', 'ô', 'õ', 'ö' -> 'o'
                    'ú', 'ù', 'û', 'ü' -> 'u'
                    'ç' -> 'c'
                    else -> ch
                }
            )
        }
        return sb.toString().replace(Regex("\\s+"), " ").trim()
    }
}
